import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  uploadManagementTenantLogoMutation,
} from "@harmonyso/tenants-sdk";
import type { UploadManagementTenantLogoResponse } from "@harmonyso/tenants-sdk";
import { cellRequestOptions } from "@/lib/cells/cells";
import { invalidateCellTenantQueries } from "@/lib/cells/cell-queries";

interface UploadLogoParams {
  file: File;
  tenantId: string;
  deploymentCell?: string | null;
}

export const useManagementTenantLogoUpload = () => {
  const queryClient = useQueryClient();

  return useMutation<UploadManagementTenantLogoResponse, Error, UploadLogoParams>({
    mutationFn: async ({ file, tenantId, deploymentCell }: UploadLogoParams) => {
      const mutationOptions = uploadManagementTenantLogoMutation();
      const result = await mutationOptions.mutationFn?.({
        ...cellRequestOptions(deploymentCell),
        path: { tenant_id: tenantId },
        body: { file: file as unknown as string },
      });

      if (!result) {
        throw new Error("Upload failed - no response from server");
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      toast.success("Logo uploaded successfully");

      invalidateCellTenantQueries(
        queryClient,
        variables.deploymentCell,
        variables.tenantId,
      );
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to upload logo. Please try again.";
      toast.error(errorMessage);
      console.error("Upload logo error:", error);
    },
  });
};